import { useEffect, useMemo } from "react";

export interface ThemeStyleProps {
  /** CSS custom-property tokens. Keys must start with "--"; non-conforming keys are dropped. */
  tokens: Record<string, string>;
  /** Class scope this style applies to. Defaults to ".sd" (the SceneDesigner root). */
  scope?: string;
  /**
   * Optional webfont @import URL the theme declares. Filtered to https Google
   * Fonts (fonts.googleapis.com / fonts.gstatic.com) before emission; anything
   * else is dropped silently. Themes hosted on other CDNs need allowlisting
   * here before they'll render.
   */
  fontImportUrl?: string | null;
  /**
   * When true, also paints html/body with the theme's surface colour so the iOS
   * safe-area strips match the app (the bottom-sheet menu uses the same token).
   * Only the standalone embed sets this — it owns its document. Left off for the
   * in-dashboard previews so we don't repaint the dashboard's own body.
   */
  paintBodyBackground?: boolean;
}

/**
 * Emits a scoped <style> block that:
 *   1. @imports the theme's webfont stylesheet (if declared + allowlisted),
 *   2. overrides CSS custom properties for the SceneDesigner subtree.
 *
 * @import must come first inside the style element per CSS spec, so the font
 * stylesheet block precedes the :root token block. Token keys are validated
 * and values pass a conservative regex to keep injection out — this still
 * runs inside the customer's host page when embedded cross-origin.
 */
export function ThemeStyle({ tokens, scope = ".sd", fontImportUrl, paintBodyBackground }: ThemeStyleProps) {
  const css = useMemo(
    () => buildCss(tokens, scope, fontImportUrl ?? null, paintBodyBackground ?? false),
    [tokens, scope, fontImportUrl, paintBodyBackground]
  );

  // When painting the document background, also drive the <meta name="theme-color">
  // so iOS Safari tints its toolbar / safe-area chrome to match the menu surface.
  // The CSS html/body background fills the in-page inset; theme-color handles the
  // browser-chrome strips Safari paints over the top/bottom. Both must match.
  const surface = tokens["--sd-eg-surface"];
  useEffect(() => {
    if (!paintBodyBackground) return;
    if (typeof surface !== "string" || !SAFE_VALUE.test(surface)) return;
    if (typeof document === "undefined") return;
    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.content = surface;
  }, [paintBodyBackground, surface]);

  if (!css) return null;
  return <style data-dconfig-theme={scope}>{css}</style>;
}

const SAFE_KEY = /^--[a-zA-Z0-9_-]+$/;
// Allow common CSS value characters (colors, sizes, fonts, rgba, var(), etc.).
// Excludes braces, semicolons in values, and quotes which could break out.
const SAFE_VALUE = /^[a-zA-Z0-9_\-#.,%() \t]+$/;

// Only Google Fonts is allowlisted today. Add hosts here as new themes need
// them; the surface stays small on purpose.
const ALLOWED_FONT_HOSTS = new Set<string>([
  "fonts.googleapis.com",
  "fonts.gstatic.com",
]);

function buildCss(
  tokens: Record<string, string>,
  scope: string,
  fontImportUrl: string | null,
  paintBodyBackground: boolean
): string {
  const parts: string[] = [];

  const safeFontUrl = sanitiseFontUrl(fontImportUrl);
  if (safeFontUrl) {
    // Quoted url("...") form is safer against future Google Fonts URLs that
    // might carry CSS-special characters; the sanitiser already strips quotes
    // and parens from the URL itself, so the closing " is guaranteed unique.
    parts.push(`@import url("${safeFontUrl}");`);
  }

  const tokenLines: string[] = [];
  for (const [k, v] of Object.entries(tokens)) {
    if (!SAFE_KEY.test(k) || typeof v !== "string" || !SAFE_VALUE.test(v)) continue;
    tokenLines.push(`  ${k}: ${v};`);
  }
  if (tokenLines.length > 0) {
    parts.push(`${scope}{\n${tokenLines.join("\n")}\n}`);
  }

  // Paint the document background with the theme's surface so iOS safe-area
  // strips match the menu. Only when the surface token is themed + a safe value.
  if (paintBodyBackground) {
    const surface = tokens["--sd-eg-surface"];
    if (typeof surface === "string" && SAFE_VALUE.test(surface)) {
      parts.push(`html,body{background:${surface};}`);
    }
  }

  return parts.join("\n");
}

function sanitiseFontUrl(raw: string | null): string | null {
  if (!raw) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;
  if (!ALLOWED_FONT_HOSTS.has(parsed.hostname.toLowerCase())) return null;
  // Reject anything with quotes or stray parens — should never happen for a
  // valid Google Fonts URL but cheap belt-and-braces against injection.
  const href = parsed.toString();
  if (/["'()]/.test(href)) return null;
  return href;
}
